import React, { useMemo } from 'react';
import './Trending.css';
import { useNavigate } from 'react-router-dom';
import { useSelector } from "react-redux";
import { selectNonstops } from "../../../features/nonstop/nonstopSlice";
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaHeart } from 'react-icons/fa';
import {
  CardS,
  Header,
  CardContainer,
  CardItem,
  CardItems,
  CardWrapper,
  ImgWrapper,
  Img,
  Text
} from './Cardscss';

// Add new styled components for enhanced UI
const FavoriteIcon = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  color: #ff6b6b;
  z-index: 2;
  opacity: ${props => props.isFavorite ? 1 : 0};
  transition: opacity 0.3s ease;
  
  ${CardItem}:hover & {
    opacity: 1;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: rgba(255, 255, 255, 0.7);
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: 15px;
    color: white;
  }
  
  p {
    font-size: 1rem;
    max-width: 500px;
    margin: 0 auto;
  }
`;

const EnhancedCardItem = styled(CardItem)`
  position: relative;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }
`;

const ChannelStats = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 10px;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 5px;
`;

function Nonstops({ searchTerm = '', favorites = [] }) {
  const nonstops = useSelector(selectNonstops);
  const navigate = useNavigate();

  // Filter channels based on search term
  const filteredChannels = useMemo(() => {
    if (!nonstops) return [];
    
    if (!searchTerm) return nonstops;
    
    const lowerSearchTerm = searchTerm.toLowerCase();
    return nonstops.filter(channel => 
      channel.title.toLowerCase().includes(lowerSearchTerm) ||
      (channel.description && channel.description.toLowerCase().includes(lowerSearchTerm))
    );
  }, [nonstops, searchTerm]);

  // Check if a channel is in favorites
  const isChannelFavorite = (channelId) => {
    return favorites.some(fav => fav.id === channelId);
  };

  const handleChannelClick = (channelId) => {
    // Use navigate instead of Link to ensure proper navigation
    navigate(`/tv/${channelId}`);
  };

  // Animation variants for staggered animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12
      }
    }
  };

  return (
    <div className="Wrapper">
      <CardS>
        {filteredChannels.length === 0 ? (
          <EmptyState>
            <h3>No TV Channels Found</h3>
            <p>
              {searchTerm 
                ? `No channels matching "${searchTerm}". Try a different search term.` 
                : "There are no TV channels available at the moment. Please check back later."}
            </p>
          </EmptyState>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <CardContainer>
              {filteredChannels.map((channel) => (
                <motion.div key={channel.id} variants={itemVariants}>
                  <CardWrapper>
                    <CardItems>
                      <EnhancedCardItem onClick={() => handleChannelClick(channel.id)}>
                        {isChannelFavorite(channel.id) && (
                          <FavoriteIcon isFavorite={true}>
                            <FaHeart size={20} />
                          </FavoriteIcon>
                        )}
                        <ImgWrapper>
                          <Img src={channel.image} alt={channel.title} />
                        </ImgWrapper>
                        <Text>
                          <h1>{channel.title}</h1>
                          {channel.description && (
                            <p>{channel.description.substring(0, 60)}{channel.description.length > 60 ? '...' : ''}</p>
                          )}
                        </Text>
                        {channel.viewCount && (
                          <ChannelStats>
                            <span>{channel.viewCount} views</span>
                          </ChannelStats>
                        )}
                      </EnhancedCardItem>
                    </CardItems>
                  </CardWrapper>
                </motion.div>
              ))}
            </CardContainer>
          </motion.div>
        )}
      </CardS>
    </div>
  );
}

export default Nonstops;

